import React, { useState } from 'react';
import '../Ship/ShipCard.css'; // Reuse ship card styles
import type { PrestigeUpgrade } from '../../services/prestigeService';
import InfoPopover from '../InfoPopover';
import { formatNumber } from '../../utils/formatNumber';

// Import icons for different branch types
import miningIcon from '../../assets/skills/mining.png';
import enginesIcon from '../../assets/skills/engines.png';
import cargoIcon from '../../assets/skills/cargo.png';
import commandIcon from '../../assets/skills/command.png'; // Assuming you have this
import fleetSizeIcon from '../../assets/skills/fleetSize.png'; // Assuming you have this

// Helper to convert numbers to Roman numerals (1-10)
const toRoman = (num: number) => {
    const romans = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
    return romans[num - 1] || num;
};

// Group upgrades by branch type
const groupUpgradesByBranch = (upgrades: PrestigeUpgrade[]) => {
    const branches: Record<string, PrestigeUpgrade[]> = {};

    upgrades.forEach(upgrade => {
        if (!branches[upgrade.branchType]) {
            branches[upgrade.branchType] = [];
        }
        branches[upgrade.branchType].push(upgrade);
    });

    // Sort upgrades within each branch by level
    Object.keys(branches).forEach(branch => {
        branches[branch].sort((a, b) => a.level - b.level);
    });

    return branches;
};

// Get icon based on branch type
const getBranchIcon = (branchType: string): string => {
    switch (branchType.toLowerCase()) {
        case 'mining':
            return miningIcon;
        case 'engines':
            return enginesIcon;
        case 'cargo':
            return cargoIcon;
        case 'command':
            return commandIcon;
        case 'fleetsize':
            return fleetSizeIcon;
        default:
            return miningIcon; // Default icon
    }
};

interface PrestigeUpgradeDisplayProps {
    upgrades: PrestigeUpgrade[];
    selectedUpgrade: PrestigeUpgrade | null;
    onSelectUpgrade: (upgrade: PrestigeUpgrade) => void;
}

const PrestigeUpgradeDisplay: React.FC<PrestigeUpgradeDisplayProps> = ({
    upgrades,
    selectedUpgrade,
    onSelectUpgrade
}) => {
    const [popoverOpenUpgrade, setPopoverOpenUpgrade] = useState<PrestigeUpgrade | null>(null);
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

    const upgradesByBranch = groupUpgradesByBranch(upgrades);
    const getAvailableUpgrades = () => {
        // We need to process upgrades to get the next available one for each branch
        const result: PrestigeUpgrade[] = [];
        
        // Process each branch of upgrades
        Object.entries(upgradesByBranch).forEach(([branchType, branchUpgrades]) => {
            // First check if this branch is maxed out (all upgrades unlocked)
            const allUnlocked = branchUpgrades.every(u => u.isUnlocked === true);
            
            if (allUnlocked) {
                // For maxed branches, find the highest level upgrade and mark it as max level
                const maxLevelUpgrade = [...branchUpgrades].sort((a, b) => b.level - a.level)[0];
                if (maxLevelUpgrade) {
                    const maxedUpgrade = {
                        ...maxLevelUpgrade,
                        isMaxLevel: true
                    };
                    result.push(maxedUpgrade);
                    console.log(`Branch ${branchType} is maxed out, adding highest level: ${maxedUpgrade.name} (${maxedUpgrade.level})`);
                }
            } else {
                // Find all non-unlocked upgrades
                const availableUpgrades = branchUpgrades.filter(u => !u.isUnlocked);
                
                // Sort by level to get the next one in sequence
                const sortedUpgrades = [...availableUpgrades].sort((a, b) => a.level - b.level);

                // For debugging - log the available upgrades for this branch
                console.log(`Branch ${branchType} upgrades: ${sortedUpgrades.map(u => `${u.name} (level ${u.level}, unlocked: ${u.isUnlocked})`).join(', ')}`);

                // Take the first (lowest level) not-unlocked upgrade from each branch
                if (sortedUpgrades.length > 0) {
                    result.push(sortedUpgrades[0]);
                } else {
                    console.warn(`Branch ${branchType} has no available upgrades but is not maxed?`);
                }
            }
        });

        console.log(`Total available upgrades: ${result.length}`);
        return result;
    };
    // Get all available upgrades
    const availableUpgrades = getAvailableUpgrades();
    return (
        <div className="prestige-upgrade-display">
            <div className="upgrade-branch-row" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
                {availableUpgrades.map((upgrade) => {                    const isSelected = selectedUpgrade?.id === upgrade.id;
                    const iconSrc = getBranchIcon(upgrade.branchType);
                    // Check if this is a max level upgrade (fully unlocked branch)
                    const isMaxLevel = upgrade.isMaxLevel === true;
                    // Check if the upgrade is already unlocked
                    const isUnlocked = upgrade.isUnlocked === true;
                    // Disable if it's a max level upgrade or already unlocked
                    const isDisabled = isMaxLevel || isUnlocked;
                    
                    // Debug log to help identify issues
                    console.log(`Upgrade ${upgrade.name} (${upgrade.id}): Level=${upgrade.level}, isMaxLevel=${isMaxLevel}, isUnlocked=${isUnlocked}, isDisabled=${isDisabled}`);

                    return (
                        <div
                            key={upgrade.id}
                            className={`upgrade-symbol-container${isSelected ? ' unlocked' : ''}${isDisabled ? ' cannot-afford' : ''}${isMaxLevel ? ' highlight-maxlevel' : ''}`}
                            onClick={() => {
                                // Simply highlight this upgrade for selection
                                // Don't do anything fancy - just tell the parent which upgrade was selected
                                if (!isDisabled) onSelectUpgrade(upgrade);
                            }}
                            title={upgrade.description}
                            style={{
                                opacity: isDisabled ? 0.6 : 1,
                                pointerEvents: isDisabled ? 'none' : 'auto',
                                position: 'relative',
                                transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                                boxShadow: isSelected ? '0 0 10px rgba(76,175,80,0.5)' : undefined,
                                margin: '5px'
                            }}
                        >
                            {/* Selected indicator */}
                            {isSelected && (
                                <div style={{
                                    position: 'absolute',
                                    top: '-10px',
                                    right: '-10px',
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: '50%',
                                    backgroundColor: 'var(--color-success, #4caf50)',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 4,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }}>
                                    <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                                </div>
                            )}                  {/* Info button */}
                            <button
                                className="upgrade-info-btn"
                                type="button"
                                tabIndex={-1}
                                onClick={e => {
                                    e.stopPropagation();
                                    setPopoverOpenUpgrade(upgrade);
                                    setPopoverAnchor(e.currentTarget);
                                }}
                                aria-label="Upgrade info"
                                style={{
                                    position: 'absolute',
                                    top: 1,
                                    right: 8,
                                    background: 'var(--color-bg-secondary, #1a2638)',
                                    border: '1px solid var(--color-border-light, #334466)',
                                    borderRadius: '50%',
                                    width: 20,
                                    height: 20,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transform: 'translate(30%, 5%)',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.10)',
                                    cursor: 'pointer',
                                    zIndex: 3,
                                    padding: 0,
                                    outline: 'none',
                                }}
                            >
                               <span style={{ fontSize: '0.95em', color: '#2196f3', fontWeight: 'bold', lineHeight: 1 }}>i</span>
                            </button>
                            <div className="upgrade-bonus" style={{
                            }}>{upgrade.branchType}</div>
                            <div className="upgrade-symbol" style={{ position: 'relative', width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {iconSrc && <img src={iconSrc} alt="" style={{ width: 45, height: 45, objectFit: 'contain', zIndex: 1, transform: 'rotate(45deg)' }} />}                                <span className={`upgrade-roman${isMaxLevel ? ' highlight-maxlevel' : ''}`} style={{
                                    zIndex: 2,
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.3rem',
                                    fontWeight: 'bold',
                                    color: isMaxLevel ? '#ffb300' : isSelected ? 'var(--color-success, #4caf50)' : 'var(--color-accent-blue, #2196f3)',
                                    textShadow: isMaxLevel ? '0 1px 2px #000, 0 0 4px #ffb300aa' : isSelected ? '0 1px 2px #000, 0 0 4px rgba(76,175,80,0.5)' : '0 1px 2px #000, 0 0 4px #2196f3aa',
                                    pointerEvents: 'none',
                                }}>{toRoman(upgrade.level || 1)}</span>
                            </div>                            <div className="upgrade-bonus" style={{
                                color: isMaxLevel ? '#ffb300' : undefined,
                                fontWeight: isMaxLevel ? 'bold' : undefined
                            }}>
                                {isMaxLevel ? 'MAX' : `+${formatNumber(upgrade.bonusValue * 100)}%`}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Centralized popover using InfoPopover */}
            {popoverOpenUpgrade && popoverAnchor && (
                <InfoPopover
                    anchorEl={popoverAnchor}
                    title={popoverOpenUpgrade.branchType + " Upgrade"}
                    onClose={() => setPopoverOpenUpgrade(null)}
                    minWidth={260}
                    maxWidth={360}
                >
                    <div>
                        <h3>{popoverOpenUpgrade.name}</h3>
                        <div style={{ marginBottom: 10, fontSize: '0.97em', opacity: 0.85 }}>{popoverOpenUpgrade.description}</div>
                        <p>Level: {popoverOpenUpgrade.level}</p>
                        <p>Bonus: +{(popoverOpenUpgrade.bonusValue * 100).toFixed(0)}%</p>
                        <p>Branch: {popoverOpenUpgrade.branchType}</p>
                    </div>
                </InfoPopover>
            )}
        </div>
    );
};

export default PrestigeUpgradeDisplay;
